import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { supabase } from '../../lib/supabase';
import { useTheme, makeThemedStyles } from '../../v10/theme';
import { GlassCard } from '../../v10/ui';
import { FloRiver } from '../FloRiver';
import { buildRiverModel, type FloRiverList, type FloRiverTask, type RiverModel, type RiverItem } from '../../lib/flo-weights';
import { FloTaskSheet, appendNumaNote } from '../../tools/NumaTool';
import { FALLBACK_BASELINE_LOAD } from '../../lib/flo-weights';
import { getFloSnapshot, greenStatus, type FloSnapshot, type GreenBandsLike } from '../../lib/flo';
import { useCompassConfig } from '../sectorConfig';
import { wakeOf } from '../gameSettings';
import { Gauge, greenBandsFor, type GaugeBand } from '../Gauge';
import type { V10Palette } from '../../v10/theme';
import type { OrderWidgetProps } from './types';

// ── Gauge scales. Each maps a reading onto the arc's 0..1 badness fraction and
// puts the GREEN zone exactly on the user's green band (Settings → Game,
// Book 2.3): Charge ≥ chargeMin · Load ≤ baseline × factor · Flow ≥ flowMin.

/** CHARGE 0–10, high is good. */
function chargeGauge(t: V10Palette, charge: number, b: GreenBandsLike): { fraction: number; bands: GaugeBand[] } {
  return { fraction: clamp01((10 - charge) / 10), bands: greenBandsFor(t, (10 - b.chargeMin) / 10) };
}

/** LOAD is on the river's weight scale; the arc spans 0–2× baseline. */
function loadGauge(t: V10Palette, load: number, b: GreenBandsLike, baseline: number): { fraction: number; bands: GaugeBand[] } {
  return { fraction: clamp01(load / (baseline * 2)), bands: greenBandsFor(t, b.loadMaxOfBaseline / 2) };
}

/** FLOW 0–100+, high is good. */
function flowGauge(t: V10Palette, score: number, b: GreenBandsLike): { fraction: number; bands: GaugeBand[] } {
  return { fraction: clamp01((100 - score) / 100), bands: greenBandsFor(t, (100 - b.flowMin) / 100) };
}

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0);

/** Compact FLO river widget — real FloRiver visualization + live metrics. */
export function NumaWidget({ userId, data, onOpen, onOpenTool, onEngage }: OrderWidgetProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const { config: compassConfig } = useCompassConfig(userId);
  const game = compassConfig.game;
  const bands = game.greenBands;
  // Charge drains from the user's wake time (Book 2.2). The shared snapshot
  // (useTodayData) uses the 07:00 default, so re-derive it here with the
  // user's morningSessionTime.
  const [ownFlo, setOwnFlo] = useState<FloSnapshot | null>(null);
  const sharedFlo = data.flo;
  const wakeKey = game.morningSessionTime;
  useEffect(() => {
    if (!userId || !sharedFlo) return;
    let cancelled = false;
    getFloSnapshot(supabase, userId, 28, wakeOf(new Date(), game))
      .then((snap) => {
        if (!cancelled) setOwnFlo(snap);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, sharedFlo, wakeKey]);
  const flo = ownFlo ?? sharedFlo;
  const [riverModel, setRiverModel] = useState<RiverModel>(() => ({
    items: [],
    bySector: {},
    totalLoad: 0,
    loadBand: 'Normal' as const,
    baseline: FALLBACK_BASELINE_LOAD,
    offenders: [],
  }));
  const [taskCategoryById, setTaskCategoryById] = useState<Map<string, string | null>>(new Map());
  const [riverLoading, setRiverLoading] = useState(true);
  const [riverError, setRiverError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [sheetItem, setSheetItem] = useState<RiverItem | null>(null);
  const [sheetMode, setSheetMode] = useState<'details' | 'reschedule'>('details');
  const [sheetMutating, setSheetMutating] = useState(false);
  const [sheetError, setSheetError] = useState<string | null>(null);
  // The three gauges always sit on ONE row — they size themselves to whatever
  // width the band gives them rather than wrapping on a phone.
  const [metricsW, setMetricsW] = useState(0);
  const gaugeSize = Math.max(84, Math.min(160, Math.floor(((metricsW || 320) - 16) / 3)));

  // Fetch tasks, lists, and settings to build river model
  useEffect(() => {
    if (!userId) {
      setRiverLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setRiverLoading(true);
        const [tasksRes, listsRes, settingsRes] = await Promise.all([
          supabase
            .from('tasks')
            .select(
              'id, list_id, group_id, sg1_id, sg2_id, value_selections, default_priority, scheduled, category, created_at, title, done, unlisted, tabled, updated_at, notes'
            )
            .eq('user_id', userId),
          supabase
            .from('task_lists')
            .select('id, name, olmps_sector')
            .eq('user_id', userId),
          supabase
            .from('user_settings')
            .select('groups, value_settings, unscheduled_task_priority_bonus')
            .eq('user_id', userId)
            .maybeSingle(),
        ]);

        if (cancelled) return;

        if (tasksRes.error || listsRes.error || settingsRes.error) {
          throw new Error(tasksRes.error?.message || listsRes.error?.message || settingsRes.error?.message);
        }

        const tasks = (tasksRes.data ?? []) as any[];
        const lists = (listsRes.data ?? []) as any[];
        const settings = settingsRes.data;

        // Build FloRiverTask array
        const floTasks: FloRiverTask[] = tasks.map((tk) => ({
          id: tk.id,
          title: tk.title ?? '',
          listId: tk.list_id,
          category: tk.category,
          groupId: tk.group_id,
          sg1Id: tk.sg1_id,
          sg2Id: tk.sg2_id,
          valueSelections: tk.value_selections,
          defaultPriority: tk.default_priority,
          etc: null,
          scheduled: tk.scheduled,
          done: !!tk.done,
          unlisted: !!tk.unlisted,
          createdAt: tk.created_at,
          updatedAt: tk.updated_at,
          isProjectLinked: false,
          tabled: !!tk.tabled,
          notes: tk.notes ?? null,
        }));

        const floLists: FloRiverList[] = lists.map((l) => ({
          id: l.id,
          name: l.name,
          sector: l.olmps_sector ?? null,
        }));

        const groups = (settings?.groups ?? []) as any[];
        const valueSettings = (settings?.value_settings ?? []) as any[];
        const unscheduledBonus = settings?.unscheduled_task_priority_bonus ?? 1;

        const model = buildRiverModel(floTasks, floLists, groups, valueSettings, unscheduledBonus);
        if (!cancelled) {
          setRiverModel(model);
          setTaskCategoryById(new Map(tasks.map((tk) => [tk.id, tk.category ?? null])));
          setRiverError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setRiverError(e instanceof Error ? e.message : 'Failed to load river');
        }
      } finally {
        if (!cancelled) setRiverLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [userId, refreshTick]);

  const handlePressItem = useCallback((item: RiverItem) => {
    setSheetItem(item);
    setSheetMode('details');
    setSheetError(null);
  }, []);

  const closeItemSheet = useCallback(() => {
    setSheetItem(null);
    setSheetMode('details');
    setSheetError(null);
  }, []);

  const markItemDone = useCallback(
    async (item: RiverItem) => {
      if (!userId) return;
      setSheetMutating(true);
      setSheetError(null);
      const { error } = await supabase
        .from('tasks')
        .update({ done: true, updated_at: new Date().toISOString(), scheduled_manual: false })
        .eq('id', item.id)
        .eq('user_id', userId);
      setSheetMutating(false);
      if (error) {
        setSheetError(error.message);
        return;
      }
      // Acting on an orb is NUMA's engagement for the day (green dot).
      onEngage?.();
      closeItemSheet();
      setRefreshTick((n) => n + 1);
      // Charge/Load/Flow gauges read the shared snapshot — reload it too.
      data.refresh();
    },
    [userId, closeItemSheet, onEngage, data]
  );

  const rescheduleItem = useCallback(
    async (item: RiverItem, scheduledValue: string | null) => {
      if (!userId) return;
      setSheetMutating(true);
      setSheetError(null);
      const { error } = await supabase
        .from('tasks')
        .update({ scheduled: scheduledValue, scheduled_manual: true, updated_at: new Date().toISOString() })
        .eq('id', item.id)
        .eq('user_id', userId);
      setSheetMutating(false);
      if (error) {
        setSheetError(error.message);
        return;
      }
      onEngage?.();
      closeItemSheet();
      setRefreshTick((n) => n + 1);
      data.refresh();
    },
    [userId, closeItemSheet, onEngage, data]
  );

  const saveStoneNote = useCallback(
    async (item: RiverItem, text: string) => {
      if (!userId) return;
      setSheetMutating(true);
      setSheetError(null);
      const err = await appendNumaNote(userId, item.id, text);
      setSheetMutating(false);
      if (err) {
        setSheetError(err);
        return;
      }
      // The line is NUMA's rep for the day (green dot).
      onEngage?.();
      closeItemSheet();
      setRefreshTick((n) => n + 1);
    },
    [userId, closeItemSheet, onEngage]
  );

  const openInKyrs = useCallback(() => {
    onOpenTool?.('kyrs', sheetItem ? { initialTaskId: sheetItem.id } : undefined);
    closeItemSheet();
  }, [onOpenTool, closeItemSheet, sheetItem]);

  // LOAD: flo.load is the raw open-priority sum (hundreds), but the gauge bands
  // are ratios of BASELINE_LOAD, which is the river's normalised-weight scale.
  // Once the river has loaded, read the gauge from the river's own total so the
  // needle is on the same scale as its bands (raw flo.load saturated red).
  const riverReady = !riverLoading && !riverError;
  const loadReading = riverReady ? riverModel.totalLoad : null;

  if (!flo) {
    return (
      <Pressable onPress={onOpen} style={styles.container}>
        <Text style={styles.emptyText}>{data.floError || 'Loading FLO…'}</Text>
      </Pressable>
    );
  }

  const greens = greenStatus(
    { charge: flo.charge, load: loadReading ?? flo.load, flow: flo.flow.score },
    bands,
    riverModel.baseline
  );

  return (
    <Pressable onPress={onOpen} style={styles.container} hitSlop={0}>
      {/* River visualization */}
      {riverLoading && riverModel.items.length === 0 ? (
        <GlassCard style={styles.riverPlaceholder}>
          <ActivityIndicator color={t.goldBright} size="small" />
          <Text style={styles.riverPlaceholderText}>Loading river…</Text>
        </GlassCard>
      ) : riverError ? (
        <GlassCard style={styles.riverPlaceholder}>
          <Text style={styles.errorText}>{riverError}</Text>
        </GlassCard>
      ) : (
        <Pressable style={styles.riverWrapper} onPress={(e) => e.stopPropagation()}>
          <FloRiver
            model={riverModel}
            view="weight"
            onPressItem={handlePressItem}
            height={170}
            showLegend={false}
          />
        </Pressable>
      )}

      {/* Metrics row: three needle gauges. Each converts its own reading into
          the shared left-to-right "badness" fraction the arc ramps along, so
          the needle position and the orb colour agree across all three even
          though a high CHARGE is good and a high LOAD is not. */}
      <View
        style={styles.metricsRow}
        onLayout={(e) => setMetricsW(Math.round(e.nativeEvent.layout.width))}
      >
        <Gauge label="CHARGE" value={flo.charge.toFixed(1)} size={gaugeSize} {...chargeGauge(t, flo.charge, bands)} />
        <Gauge
          label="LOAD"
          value={String(Math.round(loadReading ?? flo.load))}
          size={gaugeSize}
          {...loadGauge(t, loadReading ?? flo.load, bands, riverModel.baseline)}
        />
        <Gauge
          label="FLOW"
          value={String(Math.round(flo.flow.score))}
          size={gaugeSize}
          {...flowGauge(t, flo.flow.score, bands)}
        />
      </View>

      {/* Green-band status: the game's measure (Book 2.3). */}
      <View style={styles.throttledChip}>
        <Text style={styles.throttledText}>
          {greens.all
            ? 'All three in the green band'
            : `Green band: ${[greens.charge ? null : 'Charge', greens.load ? null : 'Load', greens.flow ? null : 'Flow']
                .filter(Boolean)
                .join(' · ')} out`}
        </Text>
      </View>

      <FloTaskSheet
        item={sheetItem}
        category={sheetItem ? taskCategoryById.get(sheetItem.id) ?? null : null}
        mode={sheetMode}
        mutating={sheetMutating}
        errorMessage={sheetError}
        onChangeMode={setSheetMode}
        onClose={closeItemSheet}
        onMarkDone={markItemDone}
        onReschedule={rescheduleItem}
        onOpenKyrs={openInKyrs}
        onSaveNote={saveStoneNote}
      />
    </Pressable>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
  container: {
    gap: 10,
    paddingHorizontal: 2,
  },
  riverWrapper: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  riverPlaceholder: {
    height: 170,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  riverPlaceholderText: {
    fontSize: 11,
    color: t.ivorySoft,
    fontStyle: 'italic',
  },
  errorText: {
    fontSize: 11,
    color: t.lapsed,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  metricsRow: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-around',
    alignItems: 'flex-end',
  },
  pill: {
    flex: 1,
    backgroundColor: t.gold,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pillText: {
    fontSize: 10,
    fontWeight: '700',
    color: t.onGold,
    textAlign: 'center',
  },
  throttledChip: {
    alignSelf: 'flex-start',
    backgroundColor: t.behind,
    boxShadow: `0 0 12px ${t.goldGlow}`,
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  throttledText: {
    fontSize: 9,
    fontWeight: '600',
    color: t.onGold,
  },
  emptyText: {
    fontSize: 13,
    color: t.ivorySoft,
    fontStyle: 'italic',
  },
}),
);
